import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Configuration translation mapping
const configTranslations = {
  width: 'Larghezza',
  length: 'Lunghezza', 
  height: 'Altezza',
  modules: 'Moduli',
  color: 'Colore',
  glass: 'Vetri',
  glassWalls: 'Pareti Vetrate',
  perimeterLed: 'LED Perimetrale',
  roofLed: 'LED Tetto',
  rainSensor: 'Sensore Pioggia',
  shades: 'Tende',
  shadedWalls: 'Pareti Tende',
  zanzariere: 'Zanzariere',
  motorized: 'Tetto Motorizzato',
  wallMount: 'Montaggio a Parete',
  stripRotationHorizontal: 'Apertura Verticale'
};

const colorTranslations = {
  white: 'Bianco',
  black: 'Nero',
  red: 'Rosso',
  forestgreen: 'Verde Foresta',
  '#804A00': 'Marrone',
  '#EDC9AF': 'Beige'
};

const wallTranslations = {
  fronte: 'Fronte',
  retro: 'Retro',
  sinistra: 'Sinistra',
  destra: 'Destra'
};

/**
 * Capture the 3D canvas as an image
 * @param {HTMLCanvasElement} canvas - The Three.js canvas element
 * @returns {Promise<string>} - Base64 image data URL
 */
export const captureCanvas = async (canvas) => {
  try {
    if (!canvas) {
      throw new Error('Canvas element not found');
    }
    
    // First try to get the image directly from the Three.js canvas
    let imageDataUrl;
    
    try {
      // Force multiple render frames to ensure content is fully loaded
      await new Promise(resolve => {
        let frameCount = 0;
        const renderLoop = () => {
          frameCount++;
          if (frameCount < 3) { // Render a few frames to ensure content is loaded
            requestAnimationFrame(renderLoop);
          } else {
            setTimeout(resolve, 200); // Give extra time for WebGL
          }
        };
        requestAnimationFrame(renderLoop);
      });
      
      // Try to preserve the drawing buffer for capture
      const gl = canvas.getContext('webgl') || canvas.getContext('webgl2');
      if (gl && gl.getParameter) {
        // Check if preserveDrawingBuffer is available
        const preserveDrawingBuffer = gl.getParameter(gl.getParameter(gl.CONTEXT_ATTRIBUTES))?.preserveDrawingBuffer;
        console.log('WebGL preserveDrawingBuffer:', preserveDrawingBuffer);
      }
      
      // Try to get data directly from WebGL canvas
      imageDataUrl = canvas.toDataURL('image/jpeg', 0.9); // Higher quality first
      
      // More sophisticated blank canvas detection
      const isBlankCanvas = imageDataUrl.length < 10000 || // Too small data URL
                           imageDataUrl === 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k=' ||
                           imageDataUrl.includes('data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD//2Q=='); // Another common blank canvas signature
      
      if (isBlankCanvas) {
        throw new Error('Canvas appears to be blank, black, or not rendered');
      }
      
      console.log('Direct WebGL canvas capture successful, data length:', imageDataUrl.length);
    } catch (directError) {
      console.log('Direct canvas capture failed, using html2canvas:', directError.message);
      
      // Fallback to html2canvas with optimized settings
      const screenshot = await html2canvas(canvas, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#f5f5f5',
        scale: 0.5, // Reduce scale for smaller file size
        width: canvas.width,
        height: canvas.height,
        logging: false, // Disable logging for cleaner output
        imageTimeout: 5000,
        removeContainer: true
      });
      
      // Convert to JPEG for smaller file size
      imageDataUrl = screenshot.toDataURL('image/jpeg', 0.7); // 70% quality
    }
    
    console.log('Canvas captured successfully, data URL length:', imageDataUrl.length);
    return imageDataUrl;
    
  } catch (error) {
    console.error('Error capturing canvas:', error);
    
    // Return a placeholder image if capture fails
    return createPlaceholderImage();
  }
};

/**
 * Create a placeholder image when canvas capture fails
 * @returns {string} - Base64 placeholder image
 */
const createPlaceholderImage = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 300;
  const ctx = canvas.getContext('2d');
  
  // Draw a simple placeholder
  ctx.fillStyle = '#f0f0f0';
  ctx.fillRect(0, 0, 400, 300);
  
  ctx.fillStyle = '#666';
  ctx.font = '16px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('TAAPP Pergola', 200, 140);
  ctx.fillText('Configurazione 3D', 200, 160);
  ctx.fillText('(Anteprima non disponibile)', 200, 180);
  
  return canvas.toDataURL('image/jpeg', 0.8);
};

/**
 * Format configuration data for display
 * @param {Object} config - Configuration object
 * @returns {Array} - Formatted configuration items
 */
export const formatConfigForPDF = (config) => {
  const formattedConfig = [];
  
  // Basic dimensions
  formattedConfig.push({
    label: configTranslations.width,
    value: `${config.width} m`
  });
  
  formattedConfig.push({
    label: configTranslations.length,
    value: `${config.length} m`
  });
  
  formattedConfig.push({
    label: configTranslations.height,
    value: `${config.height} m`
  });
  
  formattedConfig.push({
    label: configTranslations.modules,
    value: config.modules
  });
  
  // Area calculation
  const area = Math.round(config.width * config.length);
  formattedConfig.push({
    label: 'Area',
    value: `${area} m²`
  });
  
  // Color
  formattedConfig.push({
    label: configTranslations.color,
    value: colorTranslations[config.color] || config.color
  });
  
  // Features
  const features = [
    { key: 'glass', label: configTranslations.glass },
    { key: 'perimeterLed', label: configTranslations.perimeterLed },
    { key: 'roofLed', label: configTranslations.roofLed },
    { key: 'rainSensor', label: configTranslations.rainSensor },
    { key: 'shades', label: configTranslations.shades },
    { key: 'zanzariere', label: configTranslations.zanzariere },
    { key: 'motorized', label: configTranslations.motorized },
    { key: 'wallMount', label: configTranslations.wallMount },
    { key: 'stripRotationHorizontal', label: configTranslations.stripRotationHorizontal }
  ];
  
  features.forEach(feature => {
    if (config[feature.key]) {
      formattedConfig.push({
        label: feature.label,
        value: 'Sì'
      });
    }
  });
  
  // Glass walls (if glass is enabled)
  if (config.glass && config.glassWalls) {
    const enabledWalls = Object.keys(config.glassWalls)
      .filter(wall => config.glassWalls[wall])
      .map(wall => wallTranslations[wall])
      .join(', ');
    
    if (enabledWalls) {
      formattedConfig.push({
        label: configTranslations.glassWalls,
        value: enabledWalls
      });
    }
  }
  
  // Shaded walls (if shades are enabled)
  if (config.shades && config.shadedWalls) {
    const enabledWalls = Object.keys(config.shadedWalls)
      .filter(wall => config.shadedWalls[wall])
      .map(wall => wallTranslations[wall])
      .join(', ');
    
    if (enabledWalls) {
      formattedConfig.push({
        label: configTranslations.shadedWalls,
        value: enabledWalls
      });
    }
  }
  
  return formattedConfig;
};

/**
 * Generate and download PDF with configuration and 3D image
 * @param {Object} config - Configuration object
 * @param {string} imageDataUrl - Base64 image data URL
 * @param {string} filename - PDF filename (optional)
 */
export const generatePDF = async (config, imageDataUrl, filename = 'Configurazione_Pergola_TAAPP.pdf') => {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    // Header with company branding
    pdf.setFontSize(22);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(44, 82, 130); // Blue color
    pdf.text('TAAPP', 20, 25);
    
    pdf.setFontSize(16);
    pdf.setTextColor(100, 100, 100);
    pdf.text('Pergole Innovative', 20, 35);
    
    // Add date and separator line
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    const currentDate = new Date().toLocaleDateString('it-IT');
    pdf.text(`Data: ${currentDate}`, pageWidth - 20, 25, { align: 'right' });
    
    // Separator line
    pdf.setLineWidth(0.5);
    pdf.setDrawColor(200, 200, 200);
    pdf.line(20, 42, pageWidth - 20, 42);
    
    let yPosition = 55;
    
    // Add 3D image if available
    if (imageDataUrl) {
      try {
        // Optimize image size for PDF
        const imageWidth = 140;
        const imageHeight = 90;
        const imageX = (pageWidth - imageWidth) / 2;
        
        // Add a border around the image
        pdf.setLineWidth(1);
        pdf.setDrawColor(220, 220, 220);
        pdf.rect(imageX - 2, yPosition - 2, imageWidth + 4, imageHeight + 4);
        
        // Determine image format based on data URL
        const imageFormat = imageDataUrl.includes('data:image/jpeg') ? 'JPEG' : 'PNG';
        
        pdf.addImage(imageDataUrl, imageFormat, imageX, yPosition, imageWidth, imageHeight);
        
        // Add image caption
        pdf.setFontSize(9);
        pdf.setTextColor(100, 100, 100);
        pdf.text('Anteprima 3D della configurazione', pageWidth / 2, yPosition + imageHeight + 8, { align: 'center' });
        
        yPosition += imageHeight + 20;
        
      } catch (imageError) {
        console.warn('Error adding image to PDF:', imageError);
        // Continue without image
      }
    }
    
    // Configuration section header
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(44, 82, 130);
    pdf.text('Configurazione Selezionata', 20, yPosition);
    yPosition += 15;
    
    // Configuration details in a more structured layout
    const configItems = formatConfigForPDF(config);
    pdf.setFontSize(10);
    
    // Group items into two columns for better layout
    const leftColumnItems = [];
    const rightColumnItems = [];
    
    configItems.forEach((item, index) => {
      if (index % 2 === 0) {
        leftColumnItems.push(item);
      } else {
        rightColumnItems.push(item);
      }
    });
    
    const maxItems = Math.max(leftColumnItems.length, rightColumnItems.length);
    
    for (let i = 0; i < maxItems; i++) {
      if (yPosition > pageHeight - 25) {
        pdf.addPage();
        yPosition = 20;
      }
      
      // Left column
      if (leftColumnItems[i]) {
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(`${leftColumnItems[i].label}:`, 20, yPosition);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 60, 60);
        pdf.text(`${leftColumnItems[i].value}`, 20, yPosition + 4);
      }
      
      // Right column
      if (rightColumnItems[i]) {
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(`${rightColumnItems[i].label}:`, pageWidth / 2 + 10, yPosition);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 60, 60);
        pdf.text(`${rightColumnItems[i].value}`, pageWidth / 2 + 10, yPosition + 4);
      }
      
      yPosition += 12;
    }
    
    // Footer section
    yPosition = pageHeight - 40;
    
    // Separator line
    pdf.setLineWidth(0.5);
    pdf.setDrawColor(200, 200, 200);
    pdf.line(20, yPosition, pageWidth - 20, yPosition);
    
    // Footer content
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(44, 82, 130);
    pdf.text('TAAPP - Pergole Innovative', pageWidth / 2, yPosition + 8, { align: 'center' });
    
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text('Email: info@pergoleinnovative.it', pageWidth / 2, yPosition + 15, { align: 'center' });
    pdf.text('Questo preventivo è valido per 30 giorni dalla data di emissione', pageWidth / 2, yPosition + 22, { align: 'center' });
    
    // Save the PDF
    pdf.save(filename);
    
    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return false;
  }
};

/**
 * Main function to export configuration as PDF
 * @param {Object} config - Configuration object
 * @param {HTMLCanvasElement} canvas - Three.js canvas element
 * @param {string} filename - PDF filename (optional)
 */
export const exportConfigurationPDF = async (config, canvas, filename) => {
  try {
    // Capture the 3D scene
    const imageDataUrl = await captureCanvas(canvas);
    
    // Generate PDF
    const success = await generatePDF(config, imageDataUrl, filename);
    
    if (success) {
      console.log('PDF exported successfully');
      return { success: true, message: 'PDF esportato con successo!' };
    } else {
      throw new Error('Failed to generate PDF');
    }
  } catch (error) {
    console.error('Error exporting PDF:', error);
    return { success: false, message: 'Errore durante l\'esportazione del PDF' };
  }
};